import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Upload, FileText, CheckCircle2, AlertTriangle, Trash2, RefreshCw, ChevronDown } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;
const MB = 1024 * 1024;
const ALLOWED = ['pdf', 'docx', 'txt', 'md'];
const POLL_MS = 2000;
const DEFAULT_USAGE = { used_bytes: 0, file_count: 0, max_file_bytes: 10 * MB, max_total_bytes: 50 * MB, max_files: 30 };

const fmtSize = (b) => {
  if (b >= MB) return `${(b / MB).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB`;
  return `${Math.max(1, Math.round(b / 1024))} KB`;
};
const ext = (name) => (name.includes('.') ? name.split('.').pop().toLowerCase() : '');
const isBusy = (d) => d.status === 'reading' || d.status === 'summarizing';

const T = {
  de: {
    title: 'Wissen',
    intro: 'Lade hier Dokumente und Dateien hoch, zum Beispiel Studien, Produktblätter, Präsentationen oder Leitfäden. Die KI liest sie aus und fasst sie zusammen. Du siehst, was sie daraus mitnimmt.',
    drop: 'Dateien hierher ziehen oder klicken',
    formats: 'PDF, DOCX, TXT, MD',
    perFile: 'max. {n} MB pro Datei',
    storage: 'Speicher',
    of: 'von',
    files: 'Dateien',
    full: 'Speicher voll. Lösche Dokumente, um weitere hochzuladen.',
    empty: 'Noch keine Dokumente. Lade das erste hoch.',
    reading: 'Wird gelesen …',
    summarizing: 'Wird zusammengefasst …',
    done: 'Fertig',
    error: 'Fehlgeschlagen',
    pages: 'Seiten',
    summary: 'Worum es geht',
    digest: 'Das nimmt die KI mit',
    digestHint: 'Bei passenden Themen nutzt die KI daraus einzelne Punkte. Sie verwendet es nie zwingend, damit der Content abwechslungsreich bleibt.',
    useOn: 'Für Content nutzen',
    useOff: 'Nicht genutzt',
    resum: 'Neu zusammenfassen',
    keyPoints: 'Das Wichtigste',
    facts: 'Fakten & Zahlen',
    topics: 'Themen',
    truncated: 'Das Dokument ist sehr lang. Für die Zusammenfassung wurde der Anfang gelesen.',
    retry: 'Erneut versuchen',
    del: 'Löschen',
    confirmDel: 'Dokument löschen?',
    tooBig: (n, m) => `„${n}“ ist größer als ${m} MB.`,
    badType: (n) => `„${n}“: Dateityp nicht unterstützt.`,
    noSpace: (n) => `„${n}“ passt nicht mehr in den Speicher.`,
    maxFiles: 'Maximale Dateianzahl erreicht.',
    uploadFail: 'Upload fehlgeschlagen',
  },
  en: {
    title: 'Knowledge',
    intro: 'Upload documents and files here, such as studies, product sheets, presentations or guides. The AI reads them and summarizes them. You see what it takes from them.',
    drop: 'Drop files here or click',
    formats: 'PDF, DOCX, TXT, MD',
    perFile: 'max. {n} MB per file',
    storage: 'Storage',
    of: 'of',
    files: 'files',
    full: 'Storage full. Delete documents to upload more.',
    empty: 'No documents yet. Upload your first one.',
    reading: 'Reading …',
    summarizing: 'Summarizing …',
    done: 'Done',
    error: 'Failed',
    pages: 'pages',
    summary: 'What it is about',
    digest: 'What the AI takes from it',
    digestHint: 'For matching topics the AI uses single points from it. It never uses it by force, so the content stays varied.',
    useOn: 'Use for content',
    useOff: 'Not used',
    resum: 'Summarize again',
    keyPoints: 'Key points',
    facts: 'Facts & figures',
    topics: 'Topics',
    truncated: 'The document is very long. The beginning was read for the summary.',
    retry: 'Try again',
    del: 'Delete',
    confirmDel: 'Delete document?',
    tooBig: (n, m) => `"${n}" is larger than ${m} MB.`,
    badType: (n) => `"${n}": file type not supported.`,
    noSpace: (n) => `"${n}" no longer fits in storage.`,
    maxFiles: 'Maximum number of files reached.',
    uploadFail: 'Upload failed',
  },
};

const StatusBadge = ({ doc, t }) => {
  if (isBusy(doc)) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-[#00D9C8]" data-testid="knowledge-status-busy">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />{doc.status === 'reading' ? t.reading : t.summarizing}
      </span>
    );
  }
  if (doc.status === 'error') {
    return <span className="inline-flex items-center gap-1.5 text-xs text-[#F87171]"><AlertTriangle className="w-3.5 h-3.5" />{t.error}</span>;
  }
  return <span className="inline-flex items-center gap-1.5 text-xs text-[#34D399]"><CheckCircle2 className="w-3.5 h-3.5" />{t.done}</span>;
};

const DocCard = ({ doc, t, onDelete, onRetry, onToggle }) => {
  const [open, setOpen] = useState(false);
  const s = doc.summary;
  const canOpen = doc.status === 'done' && s;
  const inUse = doc.use_in_content !== false;
  return (
    <div className="bg-[#0F1D32] border border-[#1A2B42] rounded-xl" data-testid={`knowledge-doc-${doc.id}`}>
      <div className="flex items-center gap-3 px-3.5 py-3">
        <button
          type="button"
          disabled={!canOpen}
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="flex-1 min-w-0 flex items-center gap-3 text-left disabled:cursor-default"
        >
          <FileText className="w-5 h-5 flex-shrink-0 text-[#5A6C7D]" />
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium text-white truncate">{canOpen ? s.title : doc.filename}</span>
            <span className="block text-xs text-[#8696A9] truncate">
              {canOpen ? `${doc.filename} · ` : ''}{fmtSize(doc.size)}{doc.page_count ? ` · ${doc.page_count} ${t.pages}` : ''}
            </span>
          </span>
          <StatusBadge doc={doc} t={t} />
          {canOpen && <ChevronDown className={`w-4 h-4 flex-shrink-0 text-[#5A6C7D] transition-transform ${open ? '' : '-rotate-90'}`} />}
        </button>
        {canOpen && (
          <button
            type="button"
            role="switch"
            aria-checked={inUse}
            onClick={() => onToggle(doc.id, !inUse)}
            className="flex items-center gap-1.5 text-xs text-[#A0AEC0] hover:text-white"
            title={inUse ? t.useOn : t.useOff}
            data-testid={`knowledge-toggle-${doc.id}`}
          >
            <span className={`w-8 h-[18px] rounded-full flex items-center px-0.5 transition-colors ${inUse ? 'bg-[#00D9C8]' : 'bg-[#2A3F5C]'}`}>
              <span className={`w-3.5 h-3.5 rounded-full bg-white transition-transform ${inUse ? 'translate-x-3.5' : ''}`} />
            </span>
            <span className="hidden sm:inline">{inUse ? t.useOn : t.useOff}</span>
          </button>
        )}
        {(doc.status === 'error' && doc.text_chars > 0) && (
          <button type="button" onClick={() => onRetry(doc.id)} className="p-1.5 text-[#A0AEC0] hover:text-white" title={t.retry} aria-label={t.retry}>
            <RefreshCw className="w-4 h-4" />
          </button>
        )}
        <button type="button" onClick={() => onDelete(doc.id)} className="p-1.5 text-[#5A6C7D] hover:text-[#F87171]" title={t.del} aria-label={t.del}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {doc.status === 'error' && doc.error && (
        <p className="px-3.5 pb-3 -mt-1 text-xs text-[#F87171]">{doc.error}</p>
      )}

      {open && canOpen && (
        <div className="px-3.5 pb-4 pt-1 space-y-4 border-t border-[#1A2B42]">
          {s.digest && (
            <div className="pt-3" data-testid="knowledge-digest">
              <p className="text-[11px] uppercase tracking-wide text-[#00D9C8] mb-1">{t.digest}</p>
              <p className="text-sm text-white">{s.digest}</p>
              <p className="text-xs text-[#8696A9] mt-1.5">{t.digestHint}</p>
            </div>
          )}
          {s.key_points?.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-wide text-[#6B7C90] mb-1">{t.keyPoints}</p>
              <ul className="space-y-1.5">
                {s.key_points.map((p, i) => (
                  <li key={i} className="flex gap-2 text-sm text-[#E6EDF5]">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#00D9C8] flex-shrink-0" />{p}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {s.facts?.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-wide text-[#6B7C90] mb-1">{t.facts}</p>
              <div className="grid gap-2 sm:grid-cols-2">
                {s.facts.map((f, i) => (
                  <div key={i} className="bg-[#1A2B42] rounded-lg px-3 py-2">
                    <p className="text-sm font-medium text-white">{f.value}</p>
                    <p className="text-xs text-[#8696A9]">{f.label}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {s.topics?.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-wide text-[#6B7C90] mb-1">{t.topics}</p>
              <div className="flex flex-wrap gap-1.5">
                {s.topics.map((x, i) => <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-[#1A2B42] text-[#A0AEC0]">{x}</span>)}
              </div>
            </div>
          )}
          {doc.truncated || doc.text_chars > 60000 ? <p className="text-xs text-[#8696A9]">{t.truncated}</p> : null}
          {doc.text_chars > 0 && (
            <button type="button" onClick={() => onRetry(doc.id)} className="inline-flex items-center gap-1.5 text-xs text-[#A0AEC0] hover:text-white">
              <RefreshCw className="w-3.5 h-3.5" />{t.resum}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default function KnowledgeView() {
  const { getAuthHeader } = useAuth();
  const { lang } = useLanguage();
  const t = T[lang === 'en' ? 'en' : 'de'];
  const [docs, setDocs] = useState([]);
  const [usage, setUsage] = useState(DEFAULT_USAGE);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/knowledge`, { headers: getAuthHeader() });
      setDocs(data.documents || []);
      setUsage({ ...DEFAULT_USAGE, ...(data.usage || {}) });
    } catch { /* bleibt beim letzten Stand */ }
    finally { setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const anyBusy = docs.some(isBusy);
  useEffect(() => {
    if (!anyBusy) return undefined;
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [anyBusy, load]);

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    let used = usage.used_bytes;
    let count = usage.file_count;
    for (const file of files) {
      if (!ALLOWED.includes(ext(file.name))) { toast.error(t.badType(file.name)); continue; }
      if (file.size > usage.max_file_bytes) { toast.error(t.tooBig(file.name, Math.round(usage.max_file_bytes / MB))); continue; }
      if (count >= usage.max_files) { toast.error(t.maxFiles); break; }
      if (used + file.size > usage.max_total_bytes) { toast.error(t.noSpace(file.name)); continue; }
      try {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await axios.post(`${API}/knowledge/upload`, fd, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
        setDocs((prev) => [data, ...prev]);
        used += file.size; count += 1;
      } catch (e) {
        const d = e?.response?.data?.detail;
        toast.error(typeof d === 'string' ? d : t.uploadFail);
      }
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
    load();
  };

  const onDelete = async (id) => {
    if (!window.confirm(t.confirmDel)) return;
    try {
      await axios.delete(`${API}/knowledge/${id}`, { headers: getAuthHeader() });
      setDocs((prev) => prev.filter((d) => d.id !== id));
      load();
    } catch { toast.error(t.uploadFail); }
  };

  const onToggle = async (id, value) => {
    setDocs((prev) => prev.map((d) => (d.id === id ? { ...d, use_in_content: value } : d)));
    try {
      await axios.patch(`${API}/knowledge/${id}`, { use_in_content: value }, { headers: getAuthHeader() });
    } catch {
      setDocs((prev) => prev.map((d) => (d.id === id ? { ...d, use_in_content: !value } : d)));
      toast.error(t.uploadFail);
    }
  };

  const onRetry = async (id) => {
    try {
      await axios.post(`${API}/knowledge/${id}/retry`, {}, { headers: getAuthHeader() });
      setDocs((prev) => prev.map((d) => (d.id === id ? { ...d, status: 'summarizing', error: null } : d)));
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : t.uploadFail);
    }
  };

  const pct = Math.min(100, Math.round((usage.used_bytes / usage.max_total_bytes) * 100));
  const full = usage.used_bytes >= usage.max_total_bytes || usage.file_count >= usage.max_files;
  const barColor = pct >= 90 ? '#F87171' : pct >= 70 ? '#FBBF24' : '#00D9C8';

  return (
    <div className="space-y-4" data-testid="knowledge-view">
      <div>
        <h2 className="text-lg font-semibold text-white">{t.title}</h2>
        <p className="text-sm text-[#A0AEC0] mt-1 max-w-2xl">{t.intro}</p>
      </div>

      <div className="bg-[#0F1D32] border border-[#1A2B42] rounded-xl px-3.5 py-3" data-testid="knowledge-usage">
        <div className="flex items-baseline justify-between gap-3 text-xs text-[#A0AEC0] mb-2">
          <span>{t.storage}: <span className="text-white font-medium">{fmtSize(usage.used_bytes)}</span> {t.of} {fmtSize(usage.max_total_bytes)}</span>
          <span>{usage.file_count} {t.of} {usage.max_files} {t.files}</span>
        </div>
        <div className="h-1.5 rounded-full bg-[#1A2B42] overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: barColor }} />
        </div>
      </div>

      <label
        onDragOver={(e) => { e.preventDefault(); if (!full) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); if (!full && !uploading) uploadFiles(e.dataTransfer.files); }}
        className={`flex flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed px-4 py-8 text-center transition-colors ${full ? 'opacity-50 cursor-not-allowed border-[#1A2B42]' : dragOver ? 'border-[#00D9C8] bg-[#00D9C8]/5 cursor-pointer' : 'border-[#2A3F5C] hover:border-[#00D9C8]/60 cursor-pointer'}`}
        data-testid="knowledge-dropzone"
      >
        {uploading ? <Loader2 className="w-6 h-6 text-[#00D9C8] animate-spin" /> : <Upload className="w-6 h-6 text-[#00D9C8]" />}
        <span className="text-sm font-medium text-white">{full ? t.full : t.drop}</span>
        <span className="text-xs text-[#8696A9]">{t.formats} · {t.perFile.replace('{n}', Math.round(usage.max_file_bytes / MB))}</span>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,.docx,.txt,.md"
          className="hidden"
          disabled={full || uploading}
          onChange={(e) => uploadFiles(e.target.files)}
        />
      </label>

      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 text-[#5A6C7D] animate-spin" /></div>
      ) : docs.length === 0 ? (
        <p className="text-sm text-[#8696A9] text-center py-4">{t.empty}</p>
      ) : (
        <div className="space-y-2.5">
          {docs.map((d) => <DocCard key={d.id} doc={d} t={t} onDelete={onDelete} onRetry={onRetry} onToggle={onToggle} />)}
        </div>
      )}
    </div>
  );
}
